import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { aucklandDateKey } from './auckland-time';

@Component({
  selector: 'app-purchase-order-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="pb-24 lg:pb-8 min-h-screen lg:min-h-[calc(100vh-72px)]" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <div class="flex items-center gap-3">
          <a routerLink="/purchase-orders" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
            <i class="ti ti-arrow-left text-[20px]"></i>
          </a>
          <div class="min-w-0">
            <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ form.po_no || (isExistingOrderRoute ? 'Loading PO' : 'New PO') }}</p>
            <h1 class="text-lg font-semibold text-white/95 truncate">{{ isExistingOrderRoute ? (isLoadingOrder() ? 'Loading Purchase Order' : 'Edit Purchase Order') : 'Create Purchase Order' }}</h1>
          </div>
        </div>
      </header>

      <main class="px-4 pt-5 space-y-4 lg:px-7 lg:pt-6">
        <div class="hidden lg:flex items-center justify-between">
          <div>
            <a routerLink="/purchase-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Purchase Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950">{{ form.po_no || (isExistingOrderRoute ? 'Loading purchase order' : 'New Purchase Order') }}</h1>
          </div>
          @if (form.id && !isLoadingOrder()) {
            <div class="flex items-center gap-2">
              <button type="button" (click)="openPdf()" class="h-10 px-3 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-bold">PDF</button>
              <button type="button" (click)="sendToSupplier()" class="h-10 px-3 rounded-xl bg-sky-50 border border-sky-100 text-sky-700 text-sm font-bold">Email</button>
              <button type="button" (click)="updateStock()" [disabled]="!!form.stock_updated_at" class="h-10 px-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-bold disabled:opacity-50">Update Stock</button>
            </div>
          }
        </div>

        @if (isLoadingOrder()) {
          <section class="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-sm">
            <div class="mx-auto w-11 h-11 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center">
              <i class="ti ti-loader animate-spin text-2xl"></i>
            </div>
            <h2 class="mt-3 text-base font-bold text-slate-900">Loading purchase order</h2>
            <p class="mt-1 text-sm text-slate-500">Please wait while the order details come through.</p>
          </section>
        } @else {
        <section class="bg-white rounded-2xl border border-slate-100 p-4 space-y-4">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Order Details</p>
              <h2 class="text-base font-bold text-slate-900">{{ form.po_no || 'Draft purchase order' }}</h2>
            </div>
            @if (form.id) {
              <button type="button" (click)="openPdf()" class="w-10 h-10 rounded-xl bg-slate-50 text-slate-600 border border-slate-100 lg:hidden">
                <i class="ti ti-file-type-pdf text-xl"></i>
              </button>
            }
          </div>

          <div class="grid grid-cols-2 gap-3">
            <label class="text-xs font-semibold text-slate-500">Order date
              <input [(ngModel)]="form.order_date" type="date" class="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm">
            </label>
            <label class="text-xs font-semibold text-slate-500">GST
              <select [(ngModel)]="form.gst_mode" class="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white">
                <option value="GST">Add GST</option>
                <option value="No GST">Simple</option>
              </select>
            </label>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <label class="text-xs font-semibold text-slate-500">Supplier
              <select [(ngModel)]="form.supplier_id" class="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white">
                <option [ngValue]="null">New supplier</option>
                @for (supplier of suppliers(); track supplier.id) {
                  <option [ngValue]="supplier.id">{{ supplier.name }}</option>
                }
              </select>
            </label>
            <label class="text-xs font-semibold text-slate-500">Link
              <select [(ngModel)]="form.model_type" class="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white">
                <option value="General">General</option>
                <option value="Job">Job</option>
                <option value="Project">Project</option>
              </select>
            </label>
          </div>
          @if (form.model_type !== 'General') {
            <input [(ngModel)]="form.reference_id" placeholder="Job / Project ID" class="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm">
          }
          @if (!form.supplier_id) {
            <div class="space-y-2 rounded-2xl bg-slate-50 border border-slate-100 p-3">
              <input [(ngModel)]="form.supplier.name" placeholder="Supplier name" class="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm">
              <input [(ngModel)]="form.supplier.email" placeholder="Supplier email" class="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm">
              <input [(ngModel)]="form.supplier.phone" placeholder="Supplier phone" class="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm">
            </div>
          }
        </section>

        <section class="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <div>
            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Items</p>
            <p class="text-sm text-slate-500 mt-0.5">Add material-linked or manual purchase rows.</p>
          </div>
          <div class="relative">
            <i class="ti ti-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input [(ngModel)]="materialSearch" (input)="searchMaterials()" placeholder="Search material"
                   class="w-full h-11 pl-11 pr-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm focus:outline-none focus:border-[#1A6FA6]">
          </div>
          @if (materialResults().length > 0) {
            <div class="flex gap-2 overflow-x-auto pb-1">
              @for (mat of materialResults(); track mat.id) {
                <button type="button" (click)="addMaterialItem(mat)" class="shrink-0 max-w-[220px] px-3 py-2 rounded-2xl border border-sky-100 bg-sky-50 text-left">
                  <p class="text-xs font-bold text-sky-900 truncate">{{ mat.name }}</p>
                  <p class="text-[10px] text-sky-700">{{ mat.serial_no }} · {{ mat.unit || 'EA' }}</p>
                </button>
              }
            </div>
          }
          <button type="button" (click)="addManualItem()" class="w-full h-10 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-bold flex items-center justify-center gap-2">
            <i class="ti ti-plus"></i>Manual item
          </button>

          <div class="space-y-3">
            @for (item of form.items; track $index) {
              <div class="rounded-2xl border border-slate-100 bg-slate-50 p-3 space-y-2">
                <div class="flex gap-2">
                  <input [(ngModel)]="item.title" placeholder="Item" class="flex-1 h-10 rounded-xl border border-slate-200 px-3 text-sm font-semibold">
                  <button type="button" (click)="removeItem($index)" class="w-10 h-10 rounded-xl text-rose-500 bg-white border border-rose-100"><i class="ti ti-trash"></i></button>
                </div>
                <input [(ngModel)]="item.description" placeholder="Description" class="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
                <div class="grid grid-cols-3 gap-2">
                  <input [(ngModel)]="item.quantity" type="number" min="0" step="0.01" placeholder="Qty" class="h-10 rounded-xl border border-slate-200 px-3 text-sm">
                  <input [(ngModel)]="item.unit" placeholder="Unit" class="h-10 rounded-xl border border-slate-200 px-3 text-sm">
                  <input [(ngModel)]="item.unit_price" type="number" min="0" step="0.01" placeholder="Rate" class="h-10 rounded-xl border border-slate-200 px-3 text-sm">
                </div>
                <p class="text-right text-sm font-bold text-slate-900">{{ lineTotal(item) | currency:'NZD':'symbol':'1.2-2' }}</p>
              </div>
            }
          </div>
        </section>

        <section class="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
          <textarea [(ngModel)]="form.delivery_instructions" rows="2" placeholder="Delivery instructions" class="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"></textarea>
          <textarea [(ngModel)]="form.notes" rows="2" placeholder="Notes" class="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"></textarea>
          <div class="rounded-2xl bg-[#F8FCFE] border border-[#DCEAF3] p-3">
            <div class="flex justify-between text-sm py-1"><span class="text-slate-500">Subtotal</span><strong>{{ subtotal() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
            <div class="flex justify-between text-sm py-1"><span class="text-slate-500">GST</span><strong>{{ tax() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
            <div class="flex justify-between text-base py-2 border-t border-slate-200 mt-1"><span class="font-bold">Total</span><strong>{{ total() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
          </div>
        </section>

        <section class="grid grid-cols-1 gap-2">
          <button type="button" (click)="save()" [disabled]="isSaving() || isLoadingOrder()" class="h-12 rounded-2xl bg-[#102C41] text-white font-bold disabled:opacity-50">
            {{ isSaving() ? 'Saving...' : 'Save Purchase Order' }}
          </button>
          @if (form.id) {
            <div class="grid grid-cols-2 gap-2 lg:hidden">
              <button type="button" (click)="sendToSupplier()" class="h-11 rounded-2xl bg-sky-50 text-sky-700 border border-sky-100 font-bold">Email</button>
              <button type="button" (click)="updateStock()" [disabled]="!!form.stock_updated_at" class="h-11 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold disabled:opacity-50">Update Stock</button>
            </div>
          }
        </section>
        }
      </main>
    </div>
  `
})
export class PurchaseOrderDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  suppliers = signal<any[]>([]);
  materialResults = signal<any[]>([]);
  isSaving = signal(false);
  isLoadingOrder = signal(false);
  isExistingOrderRoute = false;
  materialSearch = '';
  form: any = this.emptyForm();

  ngOnInit() {
    this.loadSuppliers();
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isExistingOrderRoute = true;
      this.loadPurchaseOrder(id);
    } else {
      this.addManualItem();
    }
  }

  emptyForm() {
    return {
      id: null,
      po_no: '',
      supplier_id: null,
      supplier: { name: '', email: '', phone: '' },
      model_type: 'General',
      reference_id: '',
      order_date: aucklandDateKey(),
      status: 'Draft',
      gst_mode: 'GST',
      notes: '',
      delivery_instructions: '',
      public_token: '',
      stock_updated_at: '',
      items: []
    };
  }

  loadSuppliers() {
    this.api.getSuppliers().subscribe({ next: (res: any) => this.suppliers.set(res.data?.suppliers || []) });
  }

  loadPurchaseOrder(id: string | number) {
    this.isLoadingOrder.set(true);
    this.api.getPurchaseOrder(id).subscribe({
      next: (res: any) => {
        const po = res.data?.purchase_order;
        this.form = {
          ...this.emptyForm(),
          ...po,
          supplier_id: Number(po.supplier_id),
          items: (po.items || []).map((item: any) => ({ ...item, quantity: Number(item.quantity), unit_price: Number(item.unit_price) }))
        };
        this.isLoadingOrder.set(false);
      },
      error: () => {
        this.isLoadingOrder.set(false);
        showToast('error', 'Could not load purchase order.');
      }
    });
  }

  searchMaterials() {
    if (this.materialSearch.trim().length < 2) {
      this.materialResults.set([]);
      return;
    }
    this.api.getMaterials({ search: this.materialSearch, include_children: false, limit: 12 }).subscribe({
      next: (res: any) => this.materialResults.set(res.data?.materials || [])
    });
  }

  addMaterialItem(mat: any) {
    this.form.items.push({
      material_id: mat.id,
      sku: mat.serial_no,
      title: mat.name,
      description: mat.description || '',
      quantity: 1,
      unit: mat.unit || 'EA',
      unit_price: Number(mat.cost_price || 0)
    });
    this.materialSearch = '';
    this.materialResults.set([]);
  }

  addManualItem() {
    this.form.items.push({ material_id: null, sku: '', title: '', description: '', quantity: 1, unit: 'EA', unit_price: 0 });
  }

  removeItem(index: number) {
    this.form.items.splice(index, 1);
  }

  lineTotal(item: any): number {
    return Number(item.quantity || 0) * Number(item.unit_price || 0);
  }

  subtotal(): number {
    return this.form.items.reduce((sum: number, item: any) => sum + this.lineTotal(item), 0);
  }

  tax(): number {
    return this.form.gst_mode === 'GST' ? this.subtotal() * 0.15 : 0;
  }

  total(): number {
    return this.subtotal() + this.tax();
  }

  save() {
    if (!this.form.supplier_id && !this.form.supplier.name?.trim()) {
      alert('Supplier is required.');
      return;
    }
    if (!this.form.items.length) {
      alert('Add at least one item.');
      return;
    }
    this.isSaving.set(true);
    const payload = { ...this.form };
    if (payload.model_type === 'General') payload.reference_id = null;
    this.api.savePurchaseOrder(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (!this.form.id) {
          this.router.navigate(['/purchase-orders', res.data.id]);
        }
        this.loadSuppliers();
        this.loadPurchaseOrder(res.data.id);
      },
      error: (err: any) => {
        this.isSaving.set(false);
        alert(err.error?.message || 'Could not save purchase order.');
      }
    });
  }

  openPdf() {
    if (!this.form.id) return;
    const url = this.form.public_token
      ? `${this.api.baseUrl}/purchase_orders/pdf.php?token=${encodeURIComponent(this.form.public_token)}`
      : `${this.api.baseUrl}/purchase_orders/pdf.php?id=${encodeURIComponent(String(this.form.id))}`;
    window.open(url, '_blank');
  }

  sendToSupplier() {
    if (!this.form.id) return;
    this.api.sendPurchaseOrder(this.form.id).subscribe({
      next: () => {
        alert('Purchase order emailed.');
        this.loadPurchaseOrder(this.form.id);
      },
      error: (err: any) => alert(err.error?.message || 'Could not send purchase order.')
    });
  }

  updateStock() {
    if (!this.form.id || this.form.stock_updated_at) return;
    if (!confirm('Update stock from this purchase order? This can only be done once.')) return;
    this.api.updatePurchaseOrderStock(this.form.id).subscribe({
      next: () => {
        alert('Stock updated.');
        this.loadPurchaseOrder(this.form.id);
      },
      error: (err: any) => alert(err.error?.message || 'Could not update stock.')
    });
  }
}
